import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Mail, MessageCircle, Send, Instagram, Globe, Facebook, Heart, GraduationCap, Share2, Link2, Check } from "lucide-react";
import { http } from "@/lib/api";
import { LevelDots } from "@/pages/Rete";

const has = (v) => v && String(v).trim() && String(v).trim().toLowerCase() !== "no";
const webUrl = (v) => {
  if (!has(v)) return null;
  const s = String(v).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return /^[\w.-]+\.[a-z]{2,}(\/\S*)?$/i.test(s) ? `https://${s}` : null;
};
const ORDER = { "sì": 0, "più sì che no": 1, "più no che sì": 2 };

function Badge({ children }) {
  return <span className="text-[11px] uppercase tracking-widest bg-giallo text-viola px-2.5 py-1 rounded-full font-semibold">{children}</span>;
}

function Row({ label, children }) {
  if (!children) return null;
  return (
    <div className="grid sm:grid-cols-[160px_1fr] gap-1 py-3 border-b border-viola/10 dark:border-cream/10">
      <dt className="text-xs uppercase tracking-widest font-semibold text-viola/60 dark:text-cream/60">{label}</dt>
      <dd className="text-base break-words">{children}</dd>
    </div>
  );
}

function ShareBar({ name }) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const url = window.location.href;
  const text = `${name} nella Rete GSRD`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); } catch { /* chiuso dall'utente */ }
      return;
    }
    try { await navigator.clipboard.writeText(url); } catch { window.prompt("Copia questo link:", url); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  const enc = encodeURIComponent;
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-viola/20 dark:border-cream/25 px-4 py-2 text-sm font-semibold hover:border-fucsia hover:text-fucsia transition-colors";
  return (
    <div className="flex flex-wrap gap-2" data-testid="scheda-share">
      <button type="button" onClick={share} className={`${btn} bg-fucsia text-white border-fucsia hover:text-white hover:opacity-90`} data-testid="scheda-share-btn">
        {copied ? <Check size={15} aria-hidden /> : <Share2 size={15} aria-hidden />} {copied ? "Link copiato" : "Condividi"}
      </button>
      <a className={btn} href={`https://wa.me/?text=${enc(`${text}: ${url}`)}`} target="_blank" rel="noopener noreferrer" data-testid="scheda-share-whatsapp"><MessageCircle size={15} aria-hidden /> WhatsApp</a>
      <a className={btn} href={`https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`} target="_blank" rel="noopener noreferrer" data-testid="scheda-share-telegram"><Send size={15} aria-hidden /> Telegram</a>
      <a className={btn} href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noopener noreferrer" data-testid="scheda-share-facebook"><Facebook size={15} aria-hidden /> Facebook</a>
      <button type="button" className={btn} onClick={async () => { try { await navigator.clipboard.writeText(url); } catch { window.prompt("Copia questo link:", url); } setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); }} data-testid="scheda-copy-link">
        {linkCopied ? <Check size={15} aria-hidden /> : <Link2 size={15} aria-hidden />} {linkCopied ? "Link copiato" : "Copia link"}
      </button>
      <span className="sr-only" aria-live="polite">{copied || linkCopied ? "Link copiato" : ""}</span>
    </div>
  );
}

export default function ReteScheda() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    http.get(`/rete/scheda/${id}`).then((r) => {
      setP(r.data);
      document.title = `${r.data.nome} ${r.data.cognome} · Rete GSRD`;
    }).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="container-editorial py-24 dark:text-cream" data-testid="scheda-not-found">
        <h1 className="font-display text-4xl">Scheda non trovata</h1>
        <p className="mt-3">Forse il link è cambiato. Cerca la persona nella Rete.</p>
        <Link to="/rete" className="mt-6 inline-flex rounded-full bg-viola text-cream px-6 py-3 font-semibold dark:bg-giallo dark:text-viola">Vai alla Rete</Link>
      </div>
    );
  }
  if (!p) return <div className="container-editorial py-24 dark:text-cream" role="status">Caricamento…</div>;

  const name = `${p.nome} ${p.cognome}`;
  const formed = Object.entries(p.formed_groups || {}).sort((a, b) => (ORDER[a[1]] ?? 9) - (ORDER[b[1]] ?? 9));
  const insta = has(p.instagram) ? String(p.instagram).trim() : null;
  const instaUrl = insta && (insta.startsWith("http") ? insta : `https://instagram.com/${insta.replace(/^@/, "")}`);

  return (
    <div className="container-editorial py-16 lg:py-24 dark:text-cream" data-testid="page-rete-scheda">
      <Link to="/rete" className="inline-flex items-center gap-1.5 text-sm font-semibold text-viola/70 hover:text-fucsia dark:text-cream/70" data-testid="scheda-back">
        <ArrowLeft size={14} aria-hidden /> Tutta la Rete
      </Link>
      <article className="mt-6 max-w-3xl">
        <span className="pill">Rete GSRD</span>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]" data-testid="scheda-name">{name}</h1>
        {p.pronomi && <p className="mt-2 text-viola/70 dark:text-cream/70">{p.pronomi}</p>}
        {p.qualifica && <p className="mt-4 text-lg font-semibold text-fucsia">{p.qualifica}</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          {p.city && <span className="inline-flex items-center gap-1 rounded-full bg-lilla px-3 py-1 text-sm font-semibold text-viola"><MapPin size={13} aria-hidden /> {p.city}</span>}
          {p.online && <Badge>Online</Badge>}
          {p.in_studio && <Badge>In studio</Badge>}
          {p.studio_accessibile && <Badge>Studio accessibile</Badge>}
          {p.agevolate && <Badge>Tariffe agevolate</Badge>}
        </div>

        <div className="mt-8"><ShareBar name={name} /></div>

        <dl className="mt-10">
          <Row label="Approccio">{p.specifiche}</Row>
          <Row label="Trauma">{p.trauma && `Formazione sul trauma: ${p.trauma}`}</Row>
          <Row label="Utenza">{p.eta}</Row>
          <Row label="Lavoro con">{p.lavoro_con}</Row>
          <Row label="Lingue">{p.lingua}</Row>
          <Row label="Tempi">{p.tempi}</Row>
          <Row label="Tariffe">{p.agevolate_note && String(p.agevolate_note)}</Row>
          <Row label="Altro">{p.altro}</Row>
        </dl>

        {formed.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xs uppercase tracking-widest font-semibold text-viola/70 dark:text-cream/70 flex items-center gap-1.5"><GraduationCap size={14} aria-hidden /> Formata su</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {formed.map(([g, lvl]) => (
                <li key={g} className="text-sm rounded-full border border-fucsia/40 px-3 py-1 inline-flex items-center">{g}<LevelDots level={lvl} /><span className="sr-only"> ({lvl})</span></li>
              ))}
            </ul>
          </section>
        )}
        {(p.friendly_groups || []).length > 0 && (
          <section className="mt-8">
            <h2 className="text-xs uppercase tracking-widest font-semibold text-viola/70 dark:text-cream/70 flex items-center gap-1.5"><Heart size={14} aria-hidden /> Friendly con</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {p.friendly_groups.map((g) => <li key={g} className="text-sm rounded-full border border-viola/20 dark:border-cream/20 px-3 py-1">{g}</li>)}
            </ul>
          </section>
        )}

        <section className="mt-10 rounded-3xl bg-lilla/40 dark:bg-viola/30 p-6" data-testid="scheda-contacts">
          <h2 className="font-display text-2xl">Contatti</h2>
          <ul className="mt-4 space-y-2 text-base">
            {has(p.email) && <li><a className="inline-flex items-center gap-2 font-semibold hover:text-fucsia break-all" href={`mailto:${p.email}`}><Mail size={16} aria-hidden /> {p.email}</a></li>}
            {has(p.whatsapp) && <li className="inline-flex items-center gap-2 w-full"><MessageCircle size={16} aria-hidden /> WhatsApp: {p.whatsapp}</li>}
            {has(p.telegram) && <li className="inline-flex items-center gap-2 w-full"><Send size={16} aria-hidden /> Telegram: {p.telegram}</li>}
            {instaUrl && <li><a className="inline-flex items-center gap-2 font-semibold hover:text-fucsia break-all" href={instaUrl} target="_blank" rel="noopener noreferrer"><Instagram size={16} aria-hidden /> {insta}</a></li>}
            {webUrl(p.facebook) && <li><a className="inline-flex items-center gap-2 font-semibold hover:text-fucsia" href={webUrl(p.facebook)} target="_blank" rel="noopener noreferrer"><Facebook size={16} aria-hidden /> Facebook</a></li>}
            {webUrl(p.sito) && <li><a className="inline-flex items-center gap-2 font-semibold hover:text-fucsia break-all" href={webUrl(p.sito)} target="_blank" rel="noopener noreferrer"><Globe size={16} aria-hidden /> {String(p.sito).trim()}</a></li>}
          </ul>
        </section>
        <p className="mt-8 text-xs text-viola/60 dark:text-cream/60">Le informazioni sono autodichiarate dallə professionista. Sei tu? <Link to="/rete/iscriviti?modo=aggiorna" className="underline" data-testid="scheda-edit-link">Aggiorna la tua scheda</Link>.</p>
      </article>
    </div>
  );
}
